import React, { useMemo, useState } from 'react'

import { TResource, TResourceVariant } from 'types/GlobalResource'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useAvailableDashboardGroupsIncludeDashboards } from 'redux/hooks/DashboardGroups'
import { useResources } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SelectSearch from 'components_new/molecules/SelectSearch'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { useReports } from 'redux/hooks/Reports'

interface ResourcesDialogProps {
  onAdd: (id: string, variant: TResourceVariant) => void
  onClose: () => void
  onRemove: (id: string) => void
  open: boolean
  resources: TResource[]
}

const ResourcesDialog = (props: ResourcesDialogProps) => {
  const { onAdd, onClose, onRemove, open, resources } = props

  const allDashboardGroups = useAvailableDashboardGroupsIncludeDashboards()
  const reports = useReports()

  const allResources = useResources({
    dashboardGroups: allDashboardGroups,
    reports
  })

  const { isAdmin } = useAccountSettings()
  const { t } = useTranslation()

  const notAddedOptions = useMemo(
    () =>
      allResources
        .filter((resource) => !resources.some((r) => r.id === resource.id))
        .map((resource) => {
          return {
            avatar: resource.variant,
            body: t('global', resource.variant),
            href: resource.href,
            id: resource.id,
            title: resource.title,
            variant: resource.variant
          }
        }),
    [allResources, resources]
  )

  const [editMode, setEditMode] = useState<boolean>(false)

  const handleClose = () => {
    onClose()
    setEditMode(false)
  }

  return (
    <SimpleDialog
      actions={
        <Button onClick={handleClose} variant="text">
          {t('global', 'close')}
        </Button>
      }
      PaperProps={{ sx: { height: '60vh' } }}
      maxWidth="xs"
      onClose={handleClose}
      open={open}
    >
      <SectionHeader
        actions={
          resources.length > 0 && isAdmin ? (
            <Button
              onClick={() => setEditMode(!editMode)}
              size="small"
              variant={editMode ? 'contained' : 'text'}
            >
              {editMode ? t('global', 'done') : t('global', 'edit')}
            </Button>
          ) : undefined
        }
        divider={true}
        gutterBottom={true}
        title={t('global', 'resources')}
      />
      <Box>
        <Collapse in={editMode || (resources.length === 0 && isAdmin)}>
          <SelectSearch
            closeOnSelect={true}
            color="primary"
            disableClearable={true}
            label={t('global', 'add') + ' ' + t('global', 'resource', true)}
            onChange={(option) => onAdd(option.id, option.variant)}
            multiple={false}
            options={notAddedOptions}
            selected={null}
            size={'medium'}
            sort={true}
            sx={{ mb: 2 }}
          />
        </Collapse>
      </Box>

      {resources.map((resource) => {
        return (
          <GenericListItem
            action={editMode ? undefined : 'to'}
            actions={
              editMode ? (
                <Box>
                  <Tooltip title={t('global', 'delete')}>
                    <IconButton
                      onClick={() => onRemove(resource.id)}
                      size="small"
                    >
                      <Icon
                        fontSize="small"
                        name="RemoveCircleOutlineOutlined"
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : undefined
            }
            body={t('global', resource.variant)}
            hasHover={true}
            key={resource.id}
            size="medium"
            title={resource.title}
            // titleIcon={
            //   resource.isPrivate
            //     ? {
            //         name: 'Lock'
            //       }
            //     : undefined
            // }
            to={!editMode ? resource.href : undefined}
            variant={resource.variant}
          />
        )
      })}

      {/*-- empty --*/}
      {resources.length === 0 && !editMode ? (
        <EmptyPlaceholder
          background={true}
          body={t('key_metrics', 'no_resources_body')}
          size="small"
          title={t('key_metrics', 'no_resources_title')}
          sx={{ flex: 1 }}
        />
      ) : null}
    </SimpleDialog>
  )
}

export default ResourcesDialog
